<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="isLoading"
    :loading-message="spinnerText"
    button-location="top"
  >
    <template #title>
      Individual Statistics
    </template>

    <template
      v-if="isTeamLead"
      #top-form
    >
      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Operator* </label>
        <div class="col-sm-6">
          <dropdown-list
            v-model="operatorId"
            label-key="name"
            value-key="id"
            :options="operatorList"
          />
        </div>
      </div>
    </template>

    <template #content>
      <individual-statistics
        :operator-id="operatorId"
        @set-loading="setLoading"
      />
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, toRefs, onMounted, defineAsyncComponent } from 'vue';

import { useProfile } from '@/use/profile';
import { operatorService } from '@/services/operators';

const IndividualStatistics = defineAsyncComponent(() => import('./Statistics/IndividualStatistics.vue') as any);

const pageId = 'operator-individual-statistics';
const paginationLimit = 100;

const { currentUser, userRoles } = useProfile();

const { isTeamLead } = toRefs(userRoles);

const operatorId = ref('');
const operatorList = ref<{id: string; name: string}[]>([]);
const isLoading = ref(false);
const spinnerText = ref('');

const loadOperators = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * paginationLimit;

  spinnerText.value = 'Loading Operators...';
  isLoading.value = true;
  try {
    const response = await operatorService.fetchOperators({
      query: currentUser.value.id,
      isActive: true,
      pagination: {
        offset,
        limit: paginationLimit,
      },
    });

    for (const operator of response.rows) {
      operatorList.value.push({
        id: operator.id,
        name: `${operator.firstName} ${operator.lastName}`,
      });
    }

    if (operatorList.value.length < response.count) {
      return loadOperators(currentPage + 1);
    }
  } catch {
    // No action needed because this runs as a background process.
  } finally {
    isLoading.value = false;
  }
};

const setLoading = (value: boolean) => {
  spinnerText.value = 'Loading Statistics Data...';
  isLoading.value = value;
};

onMounted(() => {
  if (isTeamLead.value) {
    loadOperators();
  } else {
    operatorId.value = currentUser.value.id;
  }
});
</script>
